import * as React from 'react';
import { useDirtyStateContext } from '../contexts/DirtyStateContext';
import { usePublicAppConfig } from '../contexts/PublicAppConfigContext';

/**
 * usePageTitle - sets the browser tab title based on the current page and dirty state.
 *
 * Format:
 *   clean: "{title} - Protoss"
 *   dirty: "{title} * - Protoss"
 *
 * Reads `isAnyDirty` from DirtyStateContext so the dirty marker updates
 * automatically when any registered dirty source changes.
 *
 * Only ONE mounted component per screen should call this hook. Typically
 * MainContentShell calls it internally (using its `title` prop). Pages that
 * bypass MainContentShell call it directly or render a `<PageTitle>` component.
 */
export function usePageTitle(title: string): void {
  const { isAnyDirty } = useDirtyStateContext();
  const {
    config: { documentTitle },
  } = usePublicAppConfig();

  React.useEffect(() => {
    const marker = isAnyDirty ? ' *' : '';
    document.title = title ? `${title}${marker} - ${documentTitle}` : documentTitle;

    return () => {
      document.title = documentTitle;
    };
  }, [title, isAnyDirty, documentTitle]);
}

/**
 * PageTitle - render-nothing component that sets the browser tab title.
 *
 * Use this in JSX where calling `usePageTitle` directly is inconvenient
 * (e.g. inside conditional rendering that would break hook rules).
 */
export function PageTitle({ title }: { title: string }): null {
  usePageTitle(title);
  return null;
}
